import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './GameScore.css';
import { Plus, Eye, Trash2, Trees, Bird, Calendar } from 'lucide-react';
import { eventsService, gameService } from '../../../../../services';
import CreateEvent from './components/CreateEvent';
import EditEvent from './components/EditEvent';
import EventCard from './components/EventCard';
import EditShooters from './components/EditShooters';
import ScoreSheetManagement from './components/ScoreSheetManagement';

const GameScore = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('gameDays'); // 'gameDays', 'calendar'
    const [gameDays, setGameDays] = useState([]);
    const [gameEvents, setGameEvents] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editEventModalOpen, setEditEventModalOpen] = useState(false);
    const [editShootersModalOpen, setEditShootersModalOpen] = useState(false);
    const [scoreSheetManagementOpen, setScoreSheetManagementOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editEventInitialView, setEditEventInitialView] = useState('details');
    const [shootersRefreshKey, setShootersRefreshKey] = useState(0);

    const UK_GAME_SEASONS = {
        'Pheasant': { start: '10-01', end: '02-01', glorious: '12th October' },
        'Partridge': { start: '09-01', end: '02-01', glorious: '12th September' },
        'Grouse': { start: '08-12', end: '12-10', glorious: '12th August' },
        'Red Deer': { start: '07-01', end: '04-30' },
        'Fallow Deer': { start: '08-01', end: '05-15' },
        'Roe Deer': { start: '04-01', end: '10-31' },
        'Woodcock': { start: '10-01', end: '01-31' },
        'Snipe': { start: '08-12', end: '01-31' },
        'Mallard': { start: '09-01', end: '01-31' },
        'Pigeon': { start: '01-01', end: '12-31' },
    };

    const fetchGameDays = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await gameService.getGameDays();
            setGameDays(data);
        } catch (err) {
            console.error('Error fetching game days:', err);
            setError('Failed to load game days.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchGameEvents = useCallback(async () => {
        try {
            const data = await eventsService.getEvents('Game');
            setGameEvents(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching game events:', err);
        }
    }, []);

    useEffect(() => {
        fetchGameEvents();
        if (activeTab === 'gameDays') fetchGameDays();
        else setIsLoading(false);
    }, [activeTab, fetchGameDays, fetchGameEvents]);

    const handleDeleteGameDay = async (id) => {
        if (!window.confirm('Delete this game day record?')) return;
        try {
            await gameService.deleteGameDay(id);
            setGameDays(gameDays.filter(d => d.gameDayId !== id));
        } catch (err) {
            console.error('Error deleting game day:', err);
            setError('Failed to delete game day record.');
        }
    };

    const closeCreateModal = () => setCreateModalOpen(false);
    const closeEditEventModal = () => setEditEventModalOpen(false);
    const closeEditShootersModal = () => {
        setEditShootersModalOpen(false);
        setShootersRefreshKey((current) => current + 1);
    };
    const closeScoreSheetManagement = () => setScoreSheetManagementOpen(false);
    const openEditEventModal = (event, initialView = 'details') => {
        setSelectedEvent(event);
        setEditEventInitialView(initialView);
        setEditEventModalOpen(true);
    };
    const openEditShootersModal = (event) => {
        setSelectedEvent(event);
        setEditShootersModalOpen(true);
    };
    const openScoreSheetManagement = (event) => {
        setSelectedEvent(event);
        setScoreSheetManagementOpen(true);
    };
    const handleCreateEvent = (newEvent) => {
        setGameEvents((current) => [newEvent, ...current]);
        closeCreateModal();
    };
    const handleUpdateEvent = async (updatedEvent) => {
        try {
            const updatedEventData = await eventsService.updateEvent(updatedEvent.eventId, updatedEvent);
            setGameEvents((current) =>
                current.map((event) => event.eventId === updatedEventData.eventId ? updatedEventData : event)
            );
            closeEditEventModal();
        } catch (err) {
            console.error('Error updating game event:', err);
            setError('Failed to update game event.');
        }
    };
    const handleDeleteEvent = async (eventId) => {
        const event = gameEvents.find((entry) => entry.eventId === eventId);
        if (!window.confirm(`Delete "${event?.name}"?`)) return;
        try {
            await eventsService.deleteEvent(eventId);
            setGameEvents((current) => current.filter((entry) => entry.eventId !== eventId));
        } catch (err) {
            console.error('Error deleting game event:', err);
            setError('Failed to delete game event.');
        }
    };
    const handleShootersUpdated = useCallback(() => {
        setShootersRefreshKey((current) => current + 1);
    }, []);

    const isSpeciesInSeason = (speciesName) => {
        const season = UK_GAME_SEASONS[speciesName];
        if (!season) return null;

        const today = new Date();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const today_str = `${month}-${day}`;

        const start = season.start;
        const end = season.end;

        if (start <= end) {
            return today_str >= start && today_str <= end;
        } else {
            return today_str >= start || today_str <= end;
        }
    };

    return (
        <div className="game-score-page">
            {/* Header */}
            <div className="game-header">
                <div className="game-header-content">
                    <div className="game-header-icon">
                        <Trees size={28} />
                    </div>
                    <div className="game-header-text">
                        <h1>Game Shooting</h1>
                        <p>Track your game days, season performance, and harvest</p>
                    </div>
                </div>
                <button className="game-create-btn" onClick={() => setCreateModalOpen(true)}>
                    <Plus size={20} />
                    <span>Log Game Day</span>
                </button>
            </div>

            {/* Error Message */}
            {error && <div className="game-error-message">{error}</div>}

            {!isLoading && (
                <div className="game-events-panel">
                    <div className="game-events-panel-header">
                        <div>
                            <h3>Game Events</h3>
                            <p>Events stay tied to the venue, with extra game-day detail layered on afterwards.</p>
                        </div>
                        <button className="game-create-btn secondary" onClick={() => navigate('/dashboard/score?type=Game')}>
                            View All Game Events
                        </button>
                    </div>
                    {gameEvents.length > 0 ? (
                        <div className="game-events-grid">
                            {gameEvents.slice(0, 2).map((event) => (
                                <EventCard
                                    key={event.eventId}
                                    event={{
                                        id: event.eventId,
                                        name: event.name,
                                        eventTime: event.eventTime,
                                        eventDate: event.eventDate,
                                        clubName: event.clubName,
                                        clubId: event.clubId,
                                        eventType: 'Game',
                                        ownerName: event.ownerName,
                                        isOwner: event.isOwner,
                                        isShooter: event.isShooter,
                                        imageCount: event.imageCount || 0,
                                        coverImageUrl: event.coverImageUrl || null
                                    }}
                                    onEdit={() => openEditEventModal(event, 'details')}
                                    onEditShooters={() => openEditShootersModal(event)}
                                    onDelete={() => handleDeleteEvent(event.eventId)}
                                    onManageScoreSheets={() => openScoreSheetManagement(event)}
                                    onManageMedia={() => openEditEventModal(event, 'media')}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="game-events-empty">No game events yet. Create one before logging a full game day.</p>
                    )}
                </div>
            )}

            {/* Tabs */}
            <div className="game-tabs">
                <button
                    className={`game-tab ${activeTab === 'gameDays' ? 'active' : ''}`}
                    onClick={() => setActiveTab('gameDays')}
                >
                    <Bird size={18} />
                    Game Days
                </button>
                <button
                    className={`game-tab ${activeTab === 'calendar' ? 'active' : ''}`}
                    onClick={() => setActiveTab('calendar')}
                >
                    <Calendar size={18} />
                    Season Calendar
                </button>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="game-loading">
                    <div className="game-spinner"></div>
                    <p>Loading game data...</p>
                </div>
            )}

            {/* Game Days Tab */}
            {!isLoading && activeTab === 'gameDays' && (
                <div className="game-content">
                    {gameDays.length > 0 ? (
                        <div className="game-grid">
                            {gameDays.map(day => (
                                <div key={day.gameDayId} className="game-card">
                                    <div className="game-card-header">
                                        <h3>{day.eventName}</h3>
                                        <span className="game-type">{day.gameType}</span>
                                    </div>
                                    <div className="game-card-body">
                                        <p><strong>Location:</strong> {day.location}</p>
                                        <p><strong>Total Bag:</strong> {day.totalBag} birds</p>
                                        <p><strong>Cartridges:</strong> {day.totalCartridgesUsed}</p>
                                        <p><strong>Ratio:</strong> {day.cartridgesToBirdRatio?.toFixed(2) || 'N/A'} : 1</p>
                                        <p><strong>Drives:</strong> {day.driveCount || 0}</p>
                                        <p><strong>Date:</strong> {new Date(day.createdAt).toLocaleDateString()}</p>
                                    </div>
                                    <div className="game-card-footer">
                                        <button
                                            className="game-view-btn"
                                            onClick={() => navigate(`/dashboard/score/game/${day.gameDayId}`)}
                                        >
                                            <Eye size={16} /> View
                                        </button>
                                        <button
                                            className="game-delete-btn"
                                            onClick={() => handleDeleteGameDay(day.gameDayId)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="game-empty">
                            <Bird size={48} />
                            <h3>No Game Days Logged</h3>
                            <p>Start logging your game days to track your harvest and season performance</p>
                        </div>
                    )}
                </div>
            )}

            {/* Season Calendar Tab */}
            {!isLoading && activeTab === 'calendar' && (
                <div className="game-content">
                    <div className="game-calendar-container">
                        <h3>UK Game Seasons</h3>
                        <p className="calendar-subtitle">Glorious 12th starts the main season</p>
                        
                        <div className="game-species-grid">
                            {Object.entries(UK_GAME_SEASONS).map(([species, dates]) => {
                                const inSeason = isSpeciesInSeason(species);
                                return (
                                    <div key={species} className={`species-card ${inSeason ? 'in-season' : inSeason === false ? 'out-of-season' : ''}`}>
                                        <div className="species-card-header">
                                            <h4>{species}</h4>
                                            {inSeason && <span className="season-badge in-season-badge">Open</span>}
                                            {inSeason === false && <span className="season-badge closed-badge">Closed</span>}
                                        </div>
                                        <div className="species-card-body">
                                            <p><strong>Season:</strong> {dates.start} to {dates.end}</p>
                                            {dates.glorious && <p className="glorious-date">{dates.glorious}</p>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="game-calendar-info">
                            <p>Note: Seasons vary by region and quarry. Always check local regulations before you shoot.</p>
                        </div>
                    </div>
                </div>
            )}

            <CreateEvent
                isOpen={createModalOpen}
                closeModal={closeCreateModal}
                onEventCreated={handleCreateEvent}
                initialEventType="Game"
            />
            <EditEvent
                isOpen={editEventModalOpen}
                closeModal={closeEditEventModal}
                event={selectedEvent}
                onEventUpdated={handleUpdateEvent}
                initialView={editEventInitialView}
                canEditDetails={selectedEvent?.isOwner !== false}
                canManageMedia={selectedEvent?.isOwner !== false}
            />
            <EditShooters
                isOpen={editShootersModalOpen}
                closeModal={closeEditShootersModal}
                event={selectedEvent}
                onShootersUpdated={handleShootersUpdated}
            />
            <ScoreSheetManagement
                eventId={selectedEvent?.eventId}
                isOpen={scoreSheetManagementOpen}
                onClose={closeScoreSheetManagement}
                refreshKey={shootersRefreshKey}
                canManage={selectedEvent?.isOwner !== false}
            />
        </div>
    );
};

export default GameScore;
